import { useDataChainId } from '@ui/hooks/datachain';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import GenesisAirdrop from '@ui/services/contract/abis/GenesisAirdrop';
import { getContractAddress } from '@ui/services/contract/constants';
import { getGenesisAirdrop } from '@ui/services/contract/contract-instances/getGenesisAirdrop';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

export const useJoinTeam = ({
  code,
  onSuccess,
  onError,
  onSettled,
  onReceiptSettled,
}: {
  code: string;
} & TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const { address } = useAccount();
  const client = usePublicClient({
    chainId,
  });

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccess,
    onError,
    onSettled,
    onReceiptSettled,
  });
  const { isSimulating, simulate } = useSimulateMutation();

  const write = useCallback(async () => {
    const genesisAirdropAddress = getContractAddress('GenesisAirdrop', chainId);
    if (code.length === 0 || isNil(genesisAirdropAddress) || isNil(client)) {
      return;
    }

    const { isSuccess } = await simulate(async () => {
      const contract = getGenesisAirdrop({
        chainId,
        publicClient: client,
      });

      await contract?.estimateGas.joinTeam([code], {
        account: address,
      });
    });
    if (!isSuccess) {
      return;
    }

    return writeContract({
      abi: GenesisAirdrop,
      address: genesisAirdropAddress,
      functionName: 'joinTeam',
      args: [code],
    });
  }, [chainId, code, client, simulate, writeContract, address]);

  return useMemo(() => {
    return {
      data,
      writeContract: write,
      isPending: isSimulating || isPending,
    };
  }, [data, isPending, isSimulating, write]);
};
